import {Injectable} from '@angular/core';
import {ApiRequestService} from './api-request.service';
import {Channel} from '../model/channel';
import {Observable, Subject} from 'rxjs';
import {UserService} from './user.service';
import arrayShuffle from 'array-shuffle';
import {CustomChannelWithFavorites} from "../model/customChannelWithFavorites";
import {AppConfigService} from "../app-config.service";
import {ChannelCategory} from "../model/channelCategory";
import {ListItem} from "../model/listItem";
import {ParentCategory} from "../model/ParentCategory";
import {User} from "../model/user";
@Injectable({
    providedIn: 'root'
})
export class ChannelsService {

    private CHANNELS: Array<CustomChannelWithFavorites> = [];
    private CATEGORIES: Array<ParentCategory> = [];
    private RECENTLY_VIEWED: Array<number> = null;
    private _recentlyViewedOut: Subject<Array<number>> = new Subject();
    public recentlyViewedsReady$: Observable<Array<number>> = this._recentlyViewedOut.asObservable();
    /**
     * Channels Observer/Observable worker.
     * @type {Subject<any>}
     * @private
     */
    private _channelsOut: Subject<Array<CustomChannelWithFavorites>> = new Subject();
    /**
     *   Observable. Used for outside watchers to subscribe to.
     * @type {Observable<any>}
     */
    public channelsReady$: Observable<Array<CustomChannelWithFavorites>> = this._channelsOut.asObservable();

    private LIVE_SEARCH: string = null;
    private _liveSearchOut: Subject<string> = new Subject();
    public liveSearchReady$: Observable<string> = this._liveSearchOut.asObservable();
    public liveSearchReady( keywords: string ) {
        this._liveSearchOut.next( keywords );
    }

    public channelLoadComplete: boolean = false;
    /**
     * Channels emitter function. Used internally to notify watchers..
     * @param channels
     */
    public channelsReady( channels: Array<CustomChannelWithFavorites> ) {
        this._channelsOut.next( channels );
    }

    /**
     * Recently viewed emitter function. Used internally to notify watchers.
     */
    public recentlyViewedsReady( recentlyVieweds: Array<number>){
        this._recentlyViewedOut.next(recentlyVieweds);
    }

    public excludeCategories: string[] = [];
    /**
     * Service constructor
     * Function login is called when app is reloaded in browser. Calls to re-fetch channel list.
     * @param api
     *
     * @param userService
     */
    constructor(
        private api: ApiRequestService,
        private userService: UserService,
        private appConfig: AppConfigService
    ) {
        // console.log( "CONSTRUCTOR. Has Tokne: " + this.userService.hasAccessToken() );
        this.excludeCategories = this.appConfig.getHiddenCategories();
        if ( this.userService.hasAccessToken() ) {
            this.init();
        } else {
            this.userService.userReady$.subscribe({
                next: (user:User) => {
                    this.init();
                }
            });
        }
    }

    /**
     * Calls API for Channels and Categories.
     * Initializes Service.
     * SHOULD have a user token available.
     */
    init() {
        let me = this;
        this.api.getChannelsAndCategories().subscribe({
            next: (resp: any) => {
                me.processChannels(resp.channels);
                me.loadCategories(resp.categories);
                me.reloadRecentlyVieweds(this.userService.getRecentlyViewed());
                me.processChannelCategories();
                me._channelsOut.next(me.CHANNELS);
            }
        });
    }

    /**
     * Takes in the API feed of Categories and Initializes CATEGORIES object.
     * No Channels are added yet. Just Category structure.
     * @param categories
     */
    private loadCategories( categories: Array<ChannelCategory> ) {
        let me = this;
        this.CATEGORIES = [];
        categories.forEach( (cat:ChannelCategory) => {
            if ( !this.excludeCategories.includes( cat.title ) ) {
                let c: ParentCategory = {
                    category: cat,
                    is_visible: true,
                    subCategories: [],
                    channels: []
                };
                if (cat.parentCategory == 0) {
                    me.CATEGORIES.push(c);
                } else {
                    me.CATEGORIES.forEach((category: ParentCategory, index) => {
                        if (category.category.id == cat.parentCategory) {
                            me.CATEGORIES[index].subCategories.push(c);
                        }
                    });
                }
            }
        } );
        // console.log( "PROCESS CATEGORIES", me.CATEGORIES );
        // this._categoriesOut.next( me.CATEGORIES );
    }
    /**
     * Private function to take an array of channels from API and format them into and array matching interface
     * Initializes CHANNELS object
     * @param channels
     * @private
     */
    private processChannels( channels:Array<CustomChannelWithFavorites> ) {
        let me = this;
        // console.log( "PROCESS CHANNELS", channels );
        me.CHANNELS = [];
        if ( channels.length > 0 ) {
            channels.forEach( (element:CustomChannelWithFavorites) => {
                let c =  {
                    id: element['id'],
                    title: element['title'],
                    category: element['category'],
                    category_id: element['category_id'],
                    goname: element['goname'].replace( '#', '' ),
                    description: element['description'],
                    keywords: element['keywords'],
                    url: element['url'],
                    is_favorite: element['is_favorite'] ,
                    is_icon: element['is_icon'],
                    is_public: element['is_public'],
                    display_order: element['display_order'], // element['isicon'] // ( element['isIcon'] == 'TRUE' ? true : false )
                    is_recommended: element['is_recommended'],
                    priority: element['priority'],
                    is_new: element['is_new'],
                    include_roles: element['include_roles'],
                    exclude_roles: element['exclude_roles'],
                    eligibility: element['eligibility'],
                    has_access: me.hasChannelAccess( element ),
                    is_student_channel: element['is_student_channel'],
                    is_employee_channel: element['is_employee_channel'],
                    is_college: element['is_college'],
                    is_redirect: element['is_redirect']
                };
                me.CHANNELS.push( c );
            });
            me.channelLoadComplete = true;
        }
    }
    public findChannel( goname: string ): CustomChannelWithFavorites|null {
        let ret = null;
        if ( this.CHANNELS.length > 0 ) {
            this.CHANNELS.forEach( (c:CustomChannelWithFavorites) => {
                if ( c.goname == goname ) {
                    ret = c;
                }
            } );
        }
        return ret;
    }
    public isHiddenChannel( c: CustomChannelWithFavorites ) {
        if ( c.category == "HIDDEN" ) return true;
        else return false;
    }
    /**
     * Used on LOGIN
     * Takes a CHANNEL and CATEGORY feeds received from the /login api endpoint and initializes the service for use.
     * @param channels
     * @param categories
     */
    public processData( channels:Array<CustomChannelWithFavorites>, categories: Array<ChannelCategory> ) {
        this.reloadChannels(channels);
        this.loadCategories(categories);
        this.reloadRecentlyVieweds(this.userService.getRecentlyViewed());
        this.processChannelCategories();
        this.channelsReady( this.CHANNELS );
    }

    /**
     * Uses the CHANNELS object to fill CATEGORIES object with Channels
     */
    public processChannelCategories() {
        let me = this;
        me.CHANNELS.forEach( (c:CustomChannelWithFavorites,index:number) => {
            me.addChannelToParentCategory(c,index);
        } );
    }

    /**
     * Takes a CustomChannelWithFavorites, converts to ListItem
     * Adds it to the proper ParentCategory element
     * Use for the Category View in List page.
     * @param newChannel
     * @param newIndex
     */
    public addChannelToParentCategory( newChannel: CustomChannelWithFavorites, newIndex: number ) {
        let me = this;
        let newItem: ListItem = {
            title: newChannel.title,
            description: newChannel.description,
            keywords: newChannel.keywords,
            goname: newChannel.goname,
            is_visible: true,
            is_public: newChannel.is_public,
            included_roles: ( newChannel.include_roles == null ? [] : newChannel.include_roles.split(',') ),
            excluded_roles: ( newChannel.exclude_roles == null ? [] : newChannel.exclude_roles.split(',') ),
            has_access: this.hasChannelAccess(newChannel),
            eligibility: newChannel.eligibility,
            is_student_channel: me.channelHasStudentRole(newChannel),
            is_employee_channel: me.channelHasEmployeeRole(newChannel),
            is_college: newChannel.is_college,
            has_visibility: me.userHasVisibilityAccess()
        }
        this.CATEGORIES.forEach( (p:ParentCategory,index) => {
            if ( newChannel.category_id == p.category.id ) {
                me.CATEGORIES[index].channels.push( newItem );
                // me.CATEGORIES[index].is_visible = true;
            } else {
                if ( p.subCategories.length > 0 ) {
                    p.subCategories.forEach( (subCat:ParentCategory,index2) => {
                        if ( subCat.category.id == newChannel.category_id ) {
                            me.CATEGORIES[index].subCategories[index2].channels.push( newItem );
                            // me.CATEGORIES[index].is_visible = true;
                            // me.CATEGORIES[index].subCategories[index2].is_visible = true;
                        }
                    } );
                }
            }
        } );
    }

    /**
     * Public facing recently viewed reload
     * @param newArray Array<number>
     */
    public reloadRecentlyVieweds(newArray:Array<number>){
        this.processRecentlyVieweds(newArray);
    }

    /**
     * Public facing function reload channel list
     * Processes list and calls Observable
     * @param channels
     */
    public reloadChannels( channels:Array<CustomChannelWithFavorites> ) {
        this.processChannels( channels );
    }

    /**
     * Process Recently Viewed Channels
     * @param newArray
     */
    private processRecentlyVieweds(newArray:Array<number>){
        let me = this;
        let tempArray:Array<number> = new Array<number>();
        if(newArray != null || Array.isArray(newArray)){
            newArray.forEach(element => {
                let currentChannels = me.CHANNELS;
                if(currentChannels.filter(e => e.id == element).length > 0){
                    let currentChannel = currentChannels.find(x => x.id == element);
                    if(!currentChannel.is_favorite && !currentChannel.is_icon && !currentChannel.is_college && !currentChannel.is_recommended)
                        tempArray.push(element);
                }
            });
            if(tempArray.length > this.appConfig.getMaxRecentlyViewedSize()){
                tempArray = tempArray.splice(0, this.appConfig.getMaxRecentlyViewedSize());
            }
        }
        me.RECENTLY_VIEWED = tempArray;
        me.recentlyViewedsReady(me.RECENTLY_VIEWED);
    }

    /**
     * Deletes a recently viewed entry
     * @param channelID - the channelID to delete if it exists
     */
    public deleteRecentlyViewed(channelID: number){
        let me = this;
        let newArray = me.userService.getRecentlyViewed();
        let index = newArray.indexOf(channelID);
        if(index > -1){
            newArray.splice(index, 1);
        }
        me.userService.storeRecentlyViewed(newArray);
        me.processRecentlyVieweds(newArray);
    }

      /**
     * Updates the recently viewed locally.
     *
     * Requirements:
     * The array can only accept numbers and have a length of 3.
     * @param newChannelID - The new channel ID to add to the array.
     *  Will not add to the array if a favorite, an icon, or part of my college.
     */
    public updateRecentlyViewed(newChannelID: number){
        let me = this;
        let currentChannel = me.CHANNELS.find(x => x.id == newChannelID);

        if (!currentChannel.is_favorite && !(currentChannel.category == "Icon") && !currentChannel.is_college && !currentChannel.is_recommended && this.hasChannelAccess(currentChannel)){
            let newArray = me.userService.getRecentlyViewed();

            if(newArray.includes(newChannelID)){
                let index = newArray.indexOf(newChannelID);
                if(index > -1){
                    newArray.splice(index, 1);
                }
            }
            newArray.unshift(newChannelID);
            me.userService.storeRecentlyViewed(newArray);

            if(newArray.length > this.appConfig.getMaxRecentlyViewedSize()){
                newArray = newArray.splice(0, this.appConfig.getMaxRecentlyViewedSize());
            }
            me.api.updateRecentlyViewed(newArray).subscribe();
            me.RECENTLY_VIEWED = newArray;
            me.recentlyViewedsReady(me.RECENTLY_VIEWED );
        }

    }



    /**
     * Finds and returns a channel with matching golink ( goname ) from the current list of channels
     * @param golink
     */
    public getChannel( golink: string|null ): CustomChannelWithFavorites {
        let ret = null;
        this.CHANNELS.forEach( channel => {
            if ( channel.goname == golink ) {
                ret = channel;
            }
        } );
        return ret;
    }


    /**
     * Returns the full list of available channels in List View
     * @return Array<Channel>
     */
    public getAllChannels() {
        return this.CHANNELS;
    }

    /**
     * Returns all CATEGORIES in Category View
     */
    public getAllChannelCategories() {
        return this.CATEGORIES;
    }

    /**
     * Returns all the recently viewed
     * @return Array<number>
     */
    public getAllRecentlyViewed(){
        return this.RECENTLY_VIEWED;
    }

    /**
     * Returns all recently viewed as a custom channel favs array
     * @Return Array<CustomChannelWithFavorites>
     */
    public getRecentlyViewedAsChannelArray(): Array<CustomChannelWithFavorites>{
        let ret: Array<CustomChannelWithFavorites>  = [];
        // console.log(this.RECENTLY_VIEWED);
        if (this.RECENTLY_VIEWED != null){
            this.RECENTLY_VIEWED.forEach((element) => {
                ret.push(this.CHANNELS.find(x => x.id == element));
            });
        }

        return ret;
    }

    /**
     * Changes a favorite on a channel given the goname
     * @param isFavorite - If the channel is currently a favorite
     * @param goname - The channel we are changing the favorite and display order properties on
     * @param displayOrder - The position of the favorite
     */
    public setFavorite(isFavorite: boolean, goname: string, displayOrder: number){
        let updateChannelIndex = this.CHANNELS.findIndex(channel => channel.goname == goname);
        this.CHANNELS[updateChannelIndex].display_order = displayOrder;
        this.CHANNELS[updateChannelIndex].is_favorite = isFavorite;
        this.channelsReady(this.CHANNELS);
    }

        /**
     * Changes a favorite on a channel given the channel id
     * @param isFavorite - If the channel is currently a favorite
     * @param channelId - The channel id
     * @param displayOrder - The position of the favorite
     */
    public setFavoriteByChannelId(isFavorite: boolean, channelId: number, displayOrder: number){
        let updateChannelIndex = this.CHANNELS.findIndex(channel => channel.id == channelId);
        this.CHANNELS[updateChannelIndex].display_order = displayOrder;
        this.CHANNELS[updateChannelIndex].is_favorite = isFavorite;
        this.channelsReady(this.CHANNELS);
    }

    /**
     * Returns a filtered list of ICON channels
     * @return Array<Channel>
     */
    public getIcons() {
        let icons: Array<CustomChannelWithFavorites> = [];
        let me = this;
        me.CHANNELS.forEach( (el:CustomChannelWithFavorites) => {
            if ( el.has_access === true ) {
                if (el.is_icon == true) icons.push(el);
                else if (el.category == 'Icon') icons.push(el);
            }
        } );
        return icons;
    }

    /**
     * Gets up to three channels to recommend. Determined by priority and if there are more than three top priorities that are identical
     * it will pick three random channels.
     * @returns Three channels that are recommended
     */
    public getRecommendedChannels(): Array<CustomChannelWithFavorites> {
        let recommendedChannels: Array<CustomChannelWithFavorites> = [];
        let me = this;

        me.CHANNELS.forEach( (el:CustomChannelWithFavorites) => {
            if ( el.has_access === true && el.is_recommended === true && !el.is_favorite ) recommendedChannels.push( el );
        } );
        let recommendedChannelsToShow = 3;
        if(recommendedChannels.length > recommendedChannelsToShow){
            recommendedChannels.sort((a,b) => b.priority - a.priority);
            let highestPriority = recommendedChannels[0].priority;
            let highestPriorityChannels = recommendedChannels.filter(channel => channel.priority == highestPriority);

            if(highestPriorityChannels.length > recommendedChannelsToShow){
                highestPriorityChannels = arrayShuffle(highestPriorityChannels);
                recommendedChannels = highestPriorityChannels.splice(0, recommendedChannelsToShow);

            } else {
                recommendedChannels = recommendedChannels.splice(0, recommendedChannelsToShow);
            }
        }
        return recommendedChannels;
    }

    /**
     * Returns a filtered list of isFavourite Channels
     * @return Array<Channel>
     */
    public getFavorites() {
        let favorites: Array<CustomChannelWithFavorites> = [];
        let me = this;
        me.CHANNELS.forEach( (el:CustomChannelWithFavorites) => {
            if ( el.has_access === true && el.is_favorite === true ) favorites.push( el );
        } );
        favorites.sort((a,b) => a.display_order - b.display_order) ;
        return favorites;
    }

    /**
     * Returns a list of My College Channels
     * @return Array<Channel>
     */
    public getMyCollege() {
        let myCollege: Array<CustomChannelWithFavorites> = [];
        let me = this;
        me.CHANNELS.forEach( (el:CustomChannelWithFavorites) => {
            if ( el.has_access === true && el.is_college === true ) myCollege.push( el );
            // if ( el.has_access === true && el.category_id == me.appConfig.getMyCollegeID() ) myCollege.push( el );
        } );
        return myCollege;
    }

    /**
     * Adds a new favorite based on the channelID
     * @param channelID - The ID of the channel
     * @returns Observable<Any>
     */
    public addFavorite(channelID: number){
        let me = this;
        return me.api.addFavorite(channelID);
    }

    /**
     * Deletes a new favorite based on the channelID
     * @param channelID - The ID of the channel
     * @returns Observable<Any>
     */
    public deleteFavorite(channelID: number){
        let me = this;
        return me.api.deleteFavorite(channelID);
    }

    /**
     * Updates favorites based on the array given
     * @param channelIDs - Array of channel IDs to be updated as favorites
     * @returns - Observable<UserChannel[]>
     */
    public updateFavorite(channelIDs: number[]){
        let me = this;
        return me.api.updateFavorite(channelIDs);
    }

    /**
     * Does the provided channel have an alternate Icon Channel pointing to it.
     * @param channel
     * @returns boolean
     */
    public hasAlternateIcon( channel: Channel ) {
        let me = this;
        let icons = this.getIcons();
        let ret = false;
        icons.forEach( (i:CustomChannelWithFavorites) => {
            if ( me.getChannelURL(i).startsWith("/"+channel.goname) )
                ret = true;
        } );
        return ret;
    }

    /**
     * Helper method to return channel URL. Will consider ENV
     */
    private getChannelURL(c:Channel|CustomChannelWithFavorites): string {
        if ( c != null ) {
            return c.url;
        }
        else return '';
    }

    /** User Access and Visibility Functions */

    /**
     * Does the User have Access to the given channel
     * Return TRUE if
     *      The Channel is PUBLIC
     *      User is granted a Role assigned to the channel
     *      User is NOT granted a Role assigned to channel as an Exclusion role.
     * IMPORTANT - If the user has an inclusion role AND an exclusion role, EXCLUSION TAKES PRECEDENCE
     * @param channel
     */
    public hasChannelAccess( channel:CustomChannelWithFavorites ): boolean {
        let me = this;
        let has_access = false;
        let include_roles = ( channel.include_roles == null || channel.include_roles == '' ? [] : channel.include_roles.split(",") );
        let exclude_roles = ( channel.exclude_roles == null || channel.exclude_roles == '' ? [] : channel.exclude_roles.split(",") );
        if ( channel.is_public === true ) {
            has_access = true;
        } else {
            // If the user has any of these roles, grant access
            include_roles.forEach((c) => {
                if ( c != "" && me.userService.hasRole(c)) {
                    has_access = true;
                }
            });
            // If the user has none of these roles, grant access.
            if ( exclude_roles.length > 0 ) {
                let exclude: boolean = false;
                exclude_roles.forEach((c) => {
                    // console.log( "Exclude " + c + ": " + me.userService.hasRole(c))
                    if (c != "" && me.userService.hasRole(c)) {
                        exclude = true;
                    }
                });
                // ** IMPORTANT - If the user has an inclusion role AND an exclusion role, EXCLUSION TAKES PRECEDENCE
                if (!has_access && !exclude) has_access = true;
            }
        }
        return has_access;
    }

    /**
     * Used to determine if the user should be shown Channels they don't have access to.
     * If the user has getEmployeeVisibleRoles(), show them a filter so they can see channels with getStudentChannelRoles() and getEmployeeVisibleRoles()
     * Returns TRUE if the user is granted a role in getEmployeeVisibleRoles()
     */
    public userHasVisibilityAccess() {
        let me = this;
        let visibilityRoles = this.appConfig.getEmployeeVisibleRoles();
        let ret = false;
        if ( visibilityRoles.length > 0 ) {
            visibilityRoles.forEach( (r:string) => {
                if ( me.userService.hasRole(r) ) {
                    ret = true;
                }
            });
        }
        return ret;
    }

    /**
     * Use to classify a channel designated 'Student Channel'
     * Returns TRUE if the channels assign roles CONTAINS a role or substring in getStudentChannelRoles()
     * @param channel
     */
    public channelHasStudentRole( channel: CustomChannelWithFavorites ) {
        return channel.is_student_channel;
        // let me = this;
        // let studentRoles = me.appConfig.getStudentChannelRoles();
        // let include_roles = ( channel.include_roles == null || channel.include_roles == '' ? [] : channel.include_roles.split(",") );
        // let exclude_roles = ( channel.exclude_roles == null || channel.exclude_roles == '' ? [] : channel.exclude_roles.split(",") );
        // let ret = false;
        // if ( studentRoles.length > 0 ) {
        //     studentRoles.forEach((r:string) => {
        //         if ( include_roles.find((element) => element == r) != undefined ) ret = true;
        //     } );
        // }
        // if ( exclude_roles.find((element) => element == "student_applicant") != undefined ) {
        //     ret = true;
        // }
        // return ret;
    }
    /**
     * Use to classify a channel designated 'Employee Channel'
     * Returns TRUE if the channels assign roles CONTAINS a role or substring in getEmployeeChannelRoles()
     * @param channel
     */
    public channelHasEmployeeRole( channel: CustomChannelWithFavorites ) {
        return channel.is_employee_channel;
        // let me = this;
        // let employeeRoles = me.appConfig.getEmployeeChannelRoles();
        // let include_roles = ( channel.include_roles == null || channel.include_roles == '' ? [] : channel.include_roles.split(",") );
        // let exclude_roles = ( channel.exclude_roles == null || channel.exclude_roles == '' ? [] : channel.exclude_roles.split(",") );
        // let ret = false;
        // if ( employeeRoles.length > 0 ) {
        //     employeeRoles.forEach((r:string) => {
        //         if ( include_roles.find((element) => element == r) != undefined ) ret = true;
        //         // if ( include_roles.findIndex(element => element.includes(r)) >= 0 ) ret = true;
        //     } );
        // }
        // // if ( ret ) console.log("Employee Channel Found: " + channel.goname );
        // return ret;
    }
}
